import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, FileText, Loader2, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import ClauseFindings from "../components/ClauseFindings";
import { VERDICT_SLUG, VERDICT_TONE, verdictKey } from "./preSignatureVerdict";

// Re-export pure functions for Task 11
export { VERDICT_SLUG, VERDICT_TONE, verdictKey };

/**
 * Detail d'une analyse avant signature.
 *
 * Lit un enregistrement `decision` de mode should_i_sign via l'endpoint
 * existant GET /decisions/{id}. On NE reutilise PAS DecisionPanel : ses
 * libelles sont en anglais en dur, incompatibles avec une section i18n.
 */

function ExposureBar({ score }) {
  const { t } = useTranslation("pre_signature");
  const pct = Math.max(0, Math.min(100, score || 0));
  const tone = pct >= 70 ? "bg-rose-500" : pct >= 40 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="space-y-1.5" data-testid="presig-exposure">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-medium">
          {t("detail.exposure")}
        </span>
        <span className="font-display tabular-nums text-2xl font-semibold text-zinc-950">
          {pct}<span className="text-sm text-zinc-400">{t("detail.exposure_unit")}</span>
        </span>
      </div>
      <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function PreSignatureDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation("pre_signature");
  const [decision, setDecision] = useState(null);
  const [subject, setSubject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/decisions/${id}`)
      .then(async (res) => {
        if (cancelled) return;
        setDecision(res.data);
        const docId = (res.data?.subject_doc_ids || [])[0];
        if (!docId) return;
        try {
          const doc = await api.get(`/documents/${docId}`);
          if (!cancelled) setSubject(doc.data);
        } catch { /* le nom de fichier est cosmetique */ }
      })
      .catch((e) => {
        if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="py-20 grid place-items-center" data-testid="presig-detail-loading">
        <Loader2 size={20} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!decision) {
    return (
      <div className="py-20 text-center" data-testid="presig-detail-not-found">
        <p className="text-sm text-zinc-500">{t("detail.not_found")}</p>
        <Link to="/pre-signature" className="text-sm text-zinc-900 underline underline-offset-2 mt-3 inline-block">
          {t("detail.back")}
        </Link>
      </div>
    );
  }

  const slug = VERDICT_SLUG[decision.verdict] || "unknown";
  const recs = decision.recommendations || [];
  const analyzedAt = decision.created_at
    ? new Date(decision.created_at).toLocaleDateString(i18n.language)
    : null;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="presig-detail">
      <Link
        to="/pre-signature"
        data-testid="presig-detail-back"
        className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 transition-colors"
      >
        <ArrowLeft size={13} strokeWidth={2} /> {t("detail.back")}
      </Link>

      <header className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-medium mb-1.5">
              {t("detail.kicker")}
            </p>
            <h1
              className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 flex items-center gap-2"
              data-testid="presig-detail-document"
            >
              <FileText size={18} className="text-zinc-400 shrink-0" strokeWidth={1.75} />
              <span className="truncate">
                {subject?.filename || (decision.subject_doc_ids || [])[0]}
              </span>
            </h1>
            <p className="text-xs text-zinc-500 mt-1.5">
              {analyzedAt && t("detail.analyzed_at", { date: analyzedAt })}
              {" · "}{t("detail.confidence")}:{" "}
              <span className="font-semibold text-zinc-700">
                {Math.round((decision.confidence || 0) * 100)}%
              </span>
            </p>
          </div>
          <span
            data-testid="presig-detail-verdict"
            className={`inline-flex items-center px-3.5 py-2 rounded-full font-semibold tracking-wide text-sm ${VERDICT_TONE[slug]}`}
          >
            {t(verdictKey(decision.verdict))}
          </span>
        </div>
        <div className="mt-5 pt-5 border-t border-zinc-200">
          <ExposureBar score={decision.exposure_score} />
        </div>
      </header>

      <ClauseFindings clauses={decision.clause_findings} />

      <section data-testid="presig-detail-recommendations" className="space-y-3">
        <h3 className="font-display font-semibold text-base tracking-tight">
          {t("detail.recommendations_title")}
        </h3>
        {recs.length === 0 ? (
          <p className="text-sm text-zinc-500 italic">{t("detail.no_recommendations")}</p>
        ) : (
          <ol className="space-y-2">
            {recs.map((r, i) => (
              <li
                key={i}
                data-testid={`presig-recommendation-${i}`}
                className="flex items-start gap-3 p-3 rounded-lg border border-zinc-200 bg-white"
              >
                <CheckCircle2 size={14} className="text-zinc-400 shrink-0 mt-0.5" strokeWidth={1.75} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-950 leading-snug">{r.action}</p>
                  {r.rationale && (
                    <p className="text-xs text-zinc-600 mt-1 leading-relaxed">{r.rationale}</p>
                  )}
                  {r.blocks_signing && (
                    <span className="inline-block mt-1.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border bg-rose-50 text-rose-700 border-rose-200">
                      {t("detail.blocks_signing")}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      <Link
        to={`/decisions/${decision.id}`}
        data-testid="presig-detail-full-decision"
        className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 transition-colors"
      >
        {t("detail.open_full_decision")} <ExternalLink size={12} strokeWidth={2} />
      </Link>
    </div>
  );
}
